'use client';

import { useEffect, useId, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * A control that cannot be used right now, and says why.
 *
 * Off, the control stays focusable (aria-disabled, never the disabled
 * attribute, which drops it from the tab order and from a screen reader's
 * reach), and its reason shows in an instant tooltip on hover, on keyboard
 * focus and on a tap, and is tied to it with aria-describedby. A press while
 * off never runs the action: it flashes the reason instead.
 *
 * The same pattern as the Rota's RotaAction, kept here so any kit control can
 * take it.
 */

/** What a press on an off control does: with a reason, show it; otherwise run. */
export function offReasonPress(why: string | null | undefined): 'run' | 'flash' {
  return why ? 'flash' : 'run';
}

/**
 * Handle a press. Off with a reason, the reason flashes and the event's
 * default is prevented, so an off submit button never submits its form (it is
 * not `disabled`, so the browser would). On, the action runs.
 */
export function handleOffReasonPress(
  e: { preventDefault: () => void },
  why: string | null | undefined,
  flash: () => void,
  action?: () => void,
): void {
  if (offReasonPress(why) === 'flash') {
    e.preventDefault();
    flash();
    return;
  }
  action?.();
}

export interface OffReason {
  off: boolean;
  /** Spread on the control. Empty when it is on. */
  controlProps: {
    'aria-disabled'?: true;
    'aria-describedby'?: string;
    onPointerEnter?: (e: PointerEvent<HTMLElement>) => void;
    onPointerLeave?: (e: PointerEvent<HTMLElement>) => void;
    onFocus?: (e: { currentTarget: HTMLElement }) => void;
    onBlur?: () => void;
  };
  /** Wrap the control's click: runs `action` when on, flashes the reason when off. */
  press: (e: MouseEvent<HTMLElement>, action?: () => void) => void;
  /** Render beside the control: the reason for a screen reader, and the tooltip. */
  node: ReactNode;
}

export function useOffReason(why: string | null | undefined): OffReason {
  const id = `${useId()}-why`;
  const [at, setAt] = useState<{ x: number; y: number; below: number } | null>(null);
  const el = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const off = !!why;

  const show = (target: HTMLElement) => {
    const b = target.getBoundingClientRect();
    setAt({ x: b.left + b.width / 2, y: b.top - 6, below: b.bottom + 6 });
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setAt(null);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  // Gone on a scroll or Esc, as every kit tooltip is.
  useEffect(() => {
    if (!at) return;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setAt(null); };
    const away = () => setAt(null);
    window.addEventListener('scroll', away, true);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('keydown', key);
    };
  }, [at]);
  // Kept on screen: under the control when there is no room above, and never
  // off either side.
  useLayoutEffect(() => {
    const t = el.current;
    if (!at || !t) return;
    t.style.marginLeft = '0px';
    t.style.top = `${at.y}px`;
    t.style.transform = 'translate(-50%, -100%)';
    const r = t.getBoundingClientRect();
    const vw = window.innerWidth;
    const d = r.left < 8 ? 8 - r.left : r.right > vw - 8 ? vw - 8 - r.right : 0;
    t.style.marginLeft = `${d}px`;
    if (r.top < 8) {
      t.style.top = `${at.below}px`;
      t.style.transform = 'translate(-50%, 0)';
    }
  }, [at]);
  // Off turned on (the event went live): no tooltip left hanging.
  useEffect(() => { if (!off) setAt(null); }, [off]);

  const controlProps: OffReason['controlProps'] = off
    ? {
        'aria-disabled': true,
        'aria-describedby': id,
        onPointerEnter: (e) => { if (e.pointerType !== 'touch') show(e.currentTarget); },
        onPointerLeave: (e) => { if (e.pointerType !== 'touch') hide(); },
        onFocus: (e) => { if (e.currentTarget.matches(':focus-visible')) show(e.currentTarget); },
        onBlur: hide,
      }
    : {};

  const press = (e: MouseEvent<HTMLElement>, action?: () => void) => {
    const target = e.currentTarget;
    handleOffReasonPress(e, why, () => {
      show(target);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setAt(null), 2500);
    }, action);
  };

  const tip = off && at && typeof document !== 'undefined'
    ? createPortal(
        <span
          ref={el}
          role="tooltip"
          style={{
            position: 'fixed', left: at.x, top: at.y, transform: 'translate(-50%, -100%)', zIndex: 1100, pointerEvents: 'none',
            padding: '4px 8px', borderRadius: 6, background: 'var(--fvd-ink)', color: 'var(--fvd-canvas)', fontSize: 12, fontWeight: 500,
            lineHeight: 1.35, whiteSpace: 'normal', maxWidth: 260, width: 'max-content', boxShadow: 'var(--fvd-shadow-tooltip)',
          }}
        >
          {why}
        </span>,
        document.body,
      )
    : null;

  const node = off ? (
    <>
      <span id={id} hidden>{why}</span>
      {tip}
    </>
  ) : null;

  return { off, controlProps, press, node };
}
